import type { CreateStratInput, Scope, Strat, StratSummary, UpdateStratInput } from "@69ly/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api.ts";

const keys = {
  all: ["strats"] as const,
  list: (scope: Scope, map: string) => ["strats", "list", scope, map] as const,
  one: (id: string) => ["strats", "one", id] as const,
};

export const useStrats = (scope: Scope, map: string) =>
  useQuery({
    queryKey: keys.list(scope, map),
    queryFn: () => api<StratSummary[]>("GET", `/strats?scope=${encodeURIComponent(scope)}&map=${encodeURIComponent(map)}`),
  });

/** One board. Not refetched on focus: the board in hand may hold unsaved drawings. */
export const useStrat = (id: string | null) =>
  useQuery({
    queryKey: keys.one(id ?? ""),
    queryFn: () => api<Strat>("GET", `/strats/${id}`),
    enabled: Boolean(id),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

export function useCreateStrat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateStratInput) => api<Strat>("POST", "/strats", input),
    onSuccess: (s) => {
      qc.setQueryData(keys.one(s.id), s);
      void qc.invalidateQueries({ queryKey: ["strats", "list"] });
    },
  });
}

/** Saves a board; the answer becomes the copy in hand (its new time is the base of the next save). */
export function useSaveStrat(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateStratInput) => api<Strat>("PUT", `/strats/${id}`, input),
    onSuccess: (s) => {
      qc.setQueryData(keys.one(s.id), s);
      void qc.invalidateQueries({ queryKey: ["strats", "list"] });
    },
  });
}

export function useDeleteStrat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>("DELETE", `/strats/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
  });
}

/** Loads the board as it is on the server now, replacing the copy in hand. */
export function useReloadStrat() {
  const qc = useQueryClient();
  return (id: string) => qc.fetchQuery({ queryKey: keys.one(id), queryFn: () => api<Strat>("GET", `/strats/${id}`), staleTime: 0 });
}
